import React from "react";
import { Link } from "react-router-dom";
import { ArrowRightLeft, PlaneTakeoff, FileSignature, Radar, ArrowRight } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { resolveMedia } from "@/lib/api";
import useSEO from "@/hooks/useSEO";
import { PageHero, SectionHeading } from "@/components/primitives";
import { Reveal } from "@/components/Reveal";
import { Highlight } from "@/components/Highlight";
import ConsultingForm from "@/components/ConsultingForm";

export default function Conseil() {
  const { t, m, lang } = useApp();
  useSEO(
    { fr: "Conseil stratégique en commerce international agricole | Agrivoire Export", en: "Strategic consulting in international agricultural trade | Agrivoire Export" },
    { fr: "Entrée sur le marché, accompagnement à l'export, structuration d'accords commerciaux et veille réglementaire (IGEI).", en: "Market entry, export support, structuring trade agreements and regulatory watch (IGEI)." }
  );

  const missions = [
    { icon: ArrowRightLeft, title: t("conseil", "m_1_title"), desc: t("conseil", "m_1_desc") },
    { icon: PlaneTakeoff, title: t("conseil", "m_2_title"), desc: t("conseil", "m_2_desc") },
    { icon: FileSignature, title: t("conseil", "m_3_title"), desc: t("conseil", "m_3_desc") },
    { icon: Radar, title: t("conseil", "m_4_title"), desc: t("conseil", "m_4_desc") },
  ];

  return (
    <div data-testid="conseil-page">
      <PageHero kicker={t("conseil", "hero_kicker")} title={<Highlight text={t("conseil", "hero_title")} word={lang === "fr" ? "commerce" : "trade"} className="text-[#1CADE4]" />} image={resolveMedia(m("expertise_conseil"))} />

      <section className="mx-auto max-w-[1400px] px-5 py-20 md:px-10 md:py-24">
        <Reveal>
          <p className="max-w-3xl text-justify font-montserrat text-2xl font-medium leading-snug text-neutral-800 sm:text-3xl">{t("conseil", "intro")}</p>
        </Reveal>
      </section>

      <section className="bg-white py-20 md:py-28" data-testid="missions-section">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <SectionHeading kicker={t("conseil", "missions_kicker")} title={t("conseil", "missions_title")} />
          <div className="mt-14 grid gap-6 md:grid-cols-2">
            {missions.map((mi, i) => {
              const Icon = mi.icon;
              const highlight = i === 3;
              return (
                <Reveal key={i} delay={(i % 2) * 0.1}>
                  <div className={`h-full rounded-3xl border p-8 ${highlight ? "border-[#1CADE4]/30 bg-[#1CADE4]/5" : "border-black/5 bg-[#FAFAFA]"}`} data-testid={`mission-card-${i}`}>
                    <div className="flex items-center gap-4">
                      <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-[#8CC63F] to-[#4E7D2C] text-white">
                        <Icon className="h-5 w-5" />
                      </span>
                      <span className="font-montserrat text-sm font-bold text-[#4E7D2C]">0{i + 1}</span>
                    </div>
                    <h3 className="mt-5 font-montserrat text-xl font-bold text-neutral-900">{mi.title}</h3>
                    <p className="mt-3 font-poppins text-base leading-relaxed text-neutral-600">{mi.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-5 py-20 md:px-10 md:py-28" data-testid="conseil-forwho">
        <SectionHeading kicker={t("conseil", "forwho_kicker")} title="" />
        <Reveal>
          <div className="mt-8 rounded-3xl border border-black/5 bg-white p-8 md:p-10">
            <span className="block h-1.5 w-12 rounded-full bg-gradient-to-r from-[#8CC63F] to-[#4E7D2C]" />
            <p className="mt-6 max-w-4xl text-justify font-poppins text-lg leading-relaxed text-neutral-700">
              {t("conseil", "forwho_text")}
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="mt-10">
            <Link to="/contact#consulting-form" data-testid="conseil-cta-consultation" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#1CADE4] px-8 py-4 font-poppins text-base font-semibold text-white shadow-md transition-transform duration-300 hover:scale-105">
              {t("conseil", "cta_consultation")} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>
      </section>

      <section id="consulting-form" className="scroll-mt-24 bg-white py-20 md:py-28" data-testid="conseil-form-section">
        <div className="mx-auto max-w-3xl px-5 md:px-10">
          <Reveal>
            <p className="max-w-2xl font-montserrat text-2xl font-bold leading-snug text-neutral-900 sm:text-3xl" data-testid="conseil-form-cta">
              {t("conseil", "form_cta")}
            </p>
          </Reveal>
          <div className="mt-10"><ConsultingForm /></div>
        </div>
      </section>
    </div>
  );
}
